/**
 * 하단 이벤트 로그 (디자인): ∨ 이벤트 로그 N · Success/Error/Warning/Info 칩(누르면 그 레벨만) · 행 목록.
 * 행: 시각 · 레벨 배지 · 노드 이름 · 메시지. 오류·경고 행은 왼쪽 막대. 최신이 위. 접으면 머리 줄만 남는다.
 * 노드가 있는 행을 누르면 그 노드를 선택하고 캔버스를 이동한다.
 */
import { memo, useMemo } from "react";
import { formatLocalTime, formatTimeTooltip } from "../../lib/format";
import { filterEvents, selectActions, selectEvents, selectFilters, selectNodes, selectUiState, usePipelineStore, } from "../../store/pipeline-store";

const LEVELS = ["success", "error", "warning", "info"];
const LEVEL_LABEL = { success: "Success", error: "Error", warning: "Warning", info: "Info" };

const EventRow = memo(function EventRow({ e, nodeLabel, onPick }) {
    const clickable = !!e.nodeId;
    // 스냅샷 메시지는 "베트남 07호기 — 내용" 형태 → 노드 칸에 이름이 있으므로 앞부분을 뗀다
    const message = nodeLabel && e.message.startsWith(`${nodeLabel} — `) ? e.message.slice(nodeLabel.length + 3) : e.message;
    return (<li className={`dfv-log__row${clickable ? " is-clickable" : ""}`} data-level={e.level} onClick={clickable ? () => onPick(e.nodeId) : undefined} onKeyDown={clickable ? (ev) => { if (ev.key === "Enter") onPick(e.nodeId); } : undefined} role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined} title={formatTimeTooltip(e.timestamp)}>
      <span className="dfv-log__time">{formatLocalTime(e.timestamp)}</span>
      <span className="dfv-log__level">{LEVEL_LABEL[e.level] ?? e.level}</span>
      <span className="dfv-log__node" title={nodeLabel ?? "시스템"}>{nodeLabel ?? "시스템"}</span>
      <span className="dfv-log__msg" title={e.message}>{message}</span>
    </li>);
});

export const EventLogPanel = memo(function EventLogPanel() {
    const events = usePipelineStore(selectEvents);
    const filters = usePipelineStore(selectFilters);
    const nodes = usePipelineStore(selectNodes);
    const ui = usePipelineStore(selectUiState);
    const { setFilters, setUiState, requestFocusNode } = usePipelineStore(selectActions);
    const collapsed = ui.eventLogCollapsed;

    const labelById = useMemo(() => new Map(nodes.map((n) => [n.id, n.data.label])), [nodes]);
    const visible = useMemo(() => filterEvents(events, filters), [events, filters]); // store 는 최신순
    const levelCounts = useMemo(() => {
        const c = { info: 0, success: 0, warning: 0, error: 0 };
        for (const e of events)
            c[e.level] = (c[e.level] ?? 0) + 1;
        return c;
    }, [events]);
    const toggleLevel = (l) => setFilters({
        eventLevels: filters.eventLevels.includes(l) ? filters.eventLevels.filter((x) => x !== l) : [...filters.eventLevels, l],
    });

    return (<section className={`dfv-panel dfv-log${collapsed ? " is-collapsed" : ""}`} aria-label="이벤트 로그">
      <div className="dfv-log__head">
        <button type="button" className="dfv-log__toggle" onClick={() => setUiState({ eventLogCollapsed: !collapsed })} aria-expanded={!collapsed} title={collapsed ? "이벤트 로그 펼치기" : "이벤트 로그 접기"}>
          <span className="material-symbols-outlined" aria-hidden="true">{collapsed ? "expand_less" : "expand_more"}</span>
          이벤트 로그
          <span className="dfv-log__count">{visible.length}</span>
        </button>
        <div className="dfv-log__chips" role="group" aria-label="레벨 필터">
          {LEVELS.map((l) => (<button key={l} type="button" className={`dfv-level-chip${filters.eventLevels.includes(l) ? " is-active" : ""}`} data-level={l} aria-pressed={filters.eventLevels.includes(l)} onClick={() => toggleLevel(l)} title={`${LEVEL_LABEL[l]} 만 보기 (여러 개 선택 가능, 없으면 전체)`}>
              {LEVEL_LABEL[l]} {levelCounts[l]}
            </button>))}
        </div>
      </div>
      {!collapsed && (<ul className="dfv-log__list dfv-scroll" aria-live="polite">
          {visible.length === 0
                ? <li className="dfv-empty">{events.length === 0 ? "아직 이벤트가 없습니다." : "선택한 레벨의 이벤트가 없습니다."}</li>
                : visible.map((e) => <EventRow key={e.id} e={e} nodeLabel={e.nodeId ? labelById.get(e.nodeId) : undefined} onPick={requestFocusNode}/>)}
        </ul>)}
    </section>);
});
export default EventLogPanel;
